import { FormControl, FormGroup, Validators } from '@angular/forms';

type ReportColorVariables = {
    base: {
        backgroundColor: string;
        contrastTextColor: string;
        primaryColor: string;
        textColor: string;
        textMutedColor: string;
        textDarkColor: string;
        textContentColor: string;
        textStrongColor: string;
        iconColor: string;
        borderColor: string;
        accentBorderColor: string;
        dotLeaderColor: string;
    };
    food: {
        veryWeakReactionColor: string;
        weakReactionColor: string;
        moderateReactionColor: string;
        strongReactionColor: string;
        veryStrongReactionColor: string;
        accentColor: string;
    };
    genetic: {
        noRiskColor: string;
        gaugeBarColor: string;
        gaugeValueColor: string;
        lowRiskColor: string;
        highRiskColor: string;
        inactiveColor: string;
        highRiskOutlineColor: string;
        groupHeadingColor: string;
    };
    metabolome: {
        referenceColor: string;
        normalColor: string;
        moderateColor: string;
        alteredColor: string;
        notDeterminedColor: string;
        indicatorBorderColor: string;
    };
    microbiota: {
        chartPrimaryColor: string;
        chartSecondaryColor: string;
        chartWarningColor: string;
        dangerColor: string;
        chartPurpleColor: string;
        chartNeutralColor: string;
        rangeWarningColor: string;
        rangeSuccessColor: string;
        indicatorBorderColor: string;
        chartBackgroundColor: string;
    };
};

export const DEFAULT_PARTNER_COLOR_VARIABLES: ReportColorVariables = {
    base: {
        backgroundColor: '#FFFFFF',
        contrastTextColor: '#FFFFFF',
        primaryColor: '#003B58',
        textColor: '#807366',
        textMutedColor: '#998C80',
        textDarkColor: '#474039',
        textContentColor: '#322D28',
        textStrongColor: '#1C1917',
        iconColor: '#B0A69C',
        borderColor: '#DDD9D4',
        accentBorderColor: '#DEE687',
        dotLeaderColor: '#A8A8A8'
    },
    food: {
        veryWeakReactionColor: '#4AA91E',
        weakReactionColor: '#FADD9F',
        moderateReactionColor: '#DAA30B',
        strongReactionColor: '#D46332',
        veryStrongReactionColor: '#882624',
        accentColor: '#8DC4CF'
    },
    genetic: {
        noRiskColor: '#8DC4CF',
        gaugeBarColor: '#8DC4CF',
        gaugeValueColor: '#8DC4CF',
        lowRiskColor: '#F3BF41',
        highRiskColor: '#C11512',
        inactiveColor: '#C6BFB8',
        highRiskOutlineColor: '#882624',
        groupHeadingColor: '#63594F'
    },
    metabolome: {
        referenceColor: '#8DC4CF',
        normalColor: '#4AA91E',
        moderateColor: '#F3BF41',
        alteredColor: '#C11512',
        notDeterminedColor: '#D46332',
        indicatorBorderColor: '#989898'
    },
    microbiota: {
        chartPrimaryColor: '#005D80',
        chartSecondaryColor: '#13AFBB',
        chartWarningColor: '#DABE0B',
        dangerColor: '#C11512',
        chartPurpleColor: '#8B75B3',
        chartNeutralColor: '#5C5C5C',
        rangeWarningColor: '#FFC107',
        rangeSuccessColor: '#00D475',
        indicatorBorderColor: '#C3C3C3',
        chartBackgroundColor: 'rgba(0, 0, 0, 0)'
    }
};

const StyleSheetColorForm = (defaultValue?: string) => new FormControl(defaultValue ?? '#000000', Validators.required);

export const StylesheetBaseForms = (defaults?: ReportColorVariables['base']) => new FormGroup({
    backgroundColor: StyleSheetColorForm(defaults?.backgroundColor),
    contrastTextColor: StyleSheetColorForm(defaults?.contrastTextColor),
    primaryColor: StyleSheetColorForm(defaults?.primaryColor),
    textColor: StyleSheetColorForm(defaults?.textColor),
    textMutedColor: StyleSheetColorForm(defaults?.textMutedColor),
    textDarkColor: StyleSheetColorForm(defaults?.textDarkColor),
    textContentColor: StyleSheetColorForm(defaults?.textContentColor),
    textStrongColor: StyleSheetColorForm(defaults?.textStrongColor),
    iconColor: StyleSheetColorForm(defaults?.iconColor),
    borderColor: StyleSheetColorForm(defaults?.borderColor),
    accentBorderColor: StyleSheetColorForm(defaults?.accentBorderColor),
    dotLeaderColor: StyleSheetColorForm(defaults?.dotLeaderColor),
});

export const StylesheetFoodForms = (defaults?: ReportColorVariables['food']) => new FormGroup({
    veryWeakReactionColor: StyleSheetColorForm(defaults?.veryWeakReactionColor),
    weakReactionColor: StyleSheetColorForm(defaults?.weakReactionColor),
    moderateReactionColor: StyleSheetColorForm(defaults?.moderateReactionColor),
    strongReactionColor: StyleSheetColorForm(defaults?.strongReactionColor),
    veryStrongReactionColor: StyleSheetColorForm(defaults?.veryStrongReactionColor),
    accentColor: StyleSheetColorForm(defaults?.accentColor),
});

export const StylesheetGeneticForms = (defaults?: ReportColorVariables['genetic']) => new FormGroup({
    noRiskColor: StyleSheetColorForm(defaults?.noRiskColor),
    gaugeBarColor: StyleSheetColorForm(defaults?.gaugeBarColor),
    gaugeValueColor: StyleSheetColorForm(defaults?.gaugeValueColor),
    lowRiskColor: StyleSheetColorForm(defaults?.lowRiskColor),
    highRiskColor: StyleSheetColorForm(defaults?.highRiskColor),
    inactiveColor: StyleSheetColorForm(defaults?.inactiveColor),
    highRiskOutlineColor: StyleSheetColorForm(defaults?.highRiskOutlineColor),
    groupHeadingColor: StyleSheetColorForm(defaults?.groupHeadingColor),
});

export const StylesheetMetabolomeForms = (defaults?: ReportColorVariables['metabolome']) => new FormGroup({
    referenceColor: StyleSheetColorForm(defaults?.referenceColor),
    normalColor: StyleSheetColorForm(defaults?.normalColor),
    moderateColor: StyleSheetColorForm(defaults?.moderateColor),
    alteredColor: StyleSheetColorForm(defaults?.alteredColor),
    notDeterminedColor: StyleSheetColorForm(defaults?.notDeterminedColor),
    indicatorBorderColor: StyleSheetColorForm(defaults?.indicatorBorderColor),
});

export const StylesheetMicrobiotaForms = (defaults?: ReportColorVariables['microbiota']) => new FormGroup({
    chartPrimaryColor: StyleSheetColorForm(defaults?.chartPrimaryColor),
    chartSecondaryColor: StyleSheetColorForm(defaults?.chartSecondaryColor),
    chartWarningColor: StyleSheetColorForm(defaults?.chartWarningColor),
    dangerColor: StyleSheetColorForm(defaults?.dangerColor),
    chartPurpleColor: StyleSheetColorForm(defaults?.chartPurpleColor),
    chartNeutralColor: StyleSheetColorForm(defaults?.chartNeutralColor),
    rangeWarningColor: StyleSheetColorForm(defaults?.rangeWarningColor),
    rangeSuccessColor: StyleSheetColorForm(defaults?.rangeSuccessColor),
    indicatorBorderColor: StyleSheetColorForm(defaults?.indicatorBorderColor),
    chartBackgroundColor: StyleSheetColorForm(defaults?.chartBackgroundColor),
});
